<template>
  <div class="modal-body">
    <Title>Bulk Redtail Dossier Update</Title>
    <p class="help-text">
      Fill empty dossier fields from Redtail for the people you select.
      Existing values are not changed, and updates are applied without review or email.
    </p>

    <Callout
      v-if="errorMessage"
      status="danger"
    >
      {{ errorMessage }}
    </Callout>

    <div class="counts">
      <div class="count">
        <span class="count-label">Updated</span>
        <Badge status="success">
          <span class="tw-font-medium">{{ counts.updated }}</span>
        </Badge>
      </div>
      <div class="count">
        <span class="count-label">Unchanged</span>
        <Badge status="info">
          <span class="tw-font-medium">{{ counts.unchanged }}</span>
        </Badge>
      </div>
      <div class="count">
        <span class="count-label">Failed</span>
        <Badge status="danger">
          <span class="tw-font-medium">{{ counts.failed }}</span>
        </Badge>
      </div>
      <div class="count">
        <span class="count-label">Pending</span>
        <Badge>
          <span class="tw-font-medium">{{ counts.pending }}</span>
        </Badge>
      </div>
    </div>

    <div class="toolbar">
      <input
        v-model="search"
        class="stk-input search-input"
        type="text"
        placeholder="Search name…"
        :disabled="isLoadingPeople"
      >
      <Select
        v-model="statusFilter"
        :options="statusFilterOptions"
        placeholder="All statuses"
        clearable
        size="3_5"
      />
      <Buttons size="3_5">
        <Button
          variant="primary"
          icon-before="fas fa-download"
          :disabled="!canUpdate"
          :loading="isUpdating"
          @click="updateSelected"
        >
          Update Selected<span v-if="selectedCount"> ({{ selectedCount }})</span>
        </Button>
      </Buttons>
    </div>

    <LoaderWithText
      v-if="isLoadingPeople"
      size="3_5"
      class="tw-my-6"
    >
      Loading Redtail-linked people…
    </LoaderWithText>

    <p
      v-else-if="people.length === 0"
      class="empty"
    >
      None of your people are linked to a Redtail contact.
    </p>

    <div
      v-else
      class="table-wrap"
    >
      <table class="table">
        <thead class="head">
          <tr>
            <th class="col-head is-select">
              <label class="col-head-button">
                <input
                  type="checkbox"
                  :checked="areAllVisibleSelected"
                  :disabled="isUpdating"
                  @change="toggleSelectAll"
                >
              </label>
            </th>
            <th class="col-head">
              <span class="col-head-button">Name</span>
            </th>
            <th class="col-head">
              <span class="col-head-button">Type</span>
            </th>
            <th class="col-head">
              <span class="col-head-button">Status</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in filteredRows"
            :key="row.id"
            class="row"
          >
            <td class="cell is-select">
              <input
                type="checkbox"
                :checked="isSelected(row.id)"
                :disabled="isUpdating"
                @change="toggleRow(row.id)"
              >
            </td>
            <td class="cell">
              {{ row.name }}
            </td>
            <td class="cell">
              <Badge :status="typeBadge(row.entity_type)">
                {{ row.entity_type }}
              </Badge>
            </td>
            <td class="cell">
              <div class="status-cell">
                <Badge
                  v-if="rowStatus(row.id)"
                  :status="statusBadge(row.id)"
                >
                  {{ statusLabel(row.id) }}
                </Badge>
                <span
                  v-else
                  class="ready"
                >Ready</span>
                <small
                  v-if="rowStatus(row.id) === 'failed'"
                  class="row-error"
                >Update failed</small>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Buttons justify="between">
      <Button
        type="button"
        @click="emit('onClose')"
      >
        Close
      </Button>
    </Buttons>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import {
  Badge,
  Button,
  Buttons,
  Callout,
  LoaderWithText,
  Select,
  Title,
} from '@stakeui';

const emit = defineEmits(['onClose']);
const advisorStore = useAdvisorStore();

const people = ref([]);
const selectedIds = ref([]);
const search = ref('');
const statusFilter = ref('');
const resultsByEntity = ref({});
const errorMessage = ref('');
const isLoadingPeople = ref(false);
const isUpdating = ref(false);
const latestStatus = ref('');
let pollTimer = null;

const statusFilterOptions = [
  { value: '', label: '' },
  { value: 'pending', label: 'Pending' },
  { value: 'updated', label: 'Updated' },
  { value: 'unchanged', label: 'Unchanged' },
  { value: 'failed', label: 'Failed' },
];

const counts = computed(() => {
  const totals = { updated: 0, unchanged: 0, failed: 0, pending: 0 };
  Object.values(resultsByEntity.value || {}).forEach(result => {
    if (Object.prototype.hasOwnProperty.call(totals, result.status)) {
      totals[result.status] += 1;
    }
  });
  return totals;
});

const filteredRows = computed(() => {
  const query = search.value.trim().toLowerCase();
  return people.value.filter(row => {
    if (query && !(row.name || '').toLowerCase().includes(query)) {
      return false;
    }
    if (statusFilter.value) {
      const status = rowStatus(row.id);
      if (statusFilter.value === 'pending') {
        return !status || status === 'pending';
      }
      return status === statusFilter.value;
    }
    return true;
  });
});

const selectedCount = computed(() => selectedIds.value.length);

const areAllVisibleSelected = computed(() => {
  if (filteredRows.value.length === 0) {
    return false;
  }
  return filteredRows.value.every(row => selectedIds.value.includes(row.id));
});

const isActive = computed(() => {
  return ['pending', 'started'].includes(latestStatus.value);
});

const canUpdate = computed(() => {
  return selectedCount.value > 0 && !isUpdating.value && !isActive.value && !isLoadingPeople.value;
});

const applyLatest = latest => {
  if (!latest) {
    latestStatus.value = '';
    return;
  }
  latestStatus.value = latest.status || '';
  resultsByEntity.value = latest.meta?.results_by_entity || {};
};

const rowStatus = id => {
  return resultsByEntity.value?.[id]?.status || '';
};

const statusLabel = id => {
  const status = rowStatus(id);
  if (status === 'updated') return 'Updated';
  if (status === 'unchanged') return 'Unchanged';
  if (status === 'failed') return 'Failed';
  if (status === 'pending') return 'Pending';
  if (status === 'superseded') return 'Superseded';
  return 'Ready';
};

const statusBadge = id => {
  const status = rowStatus(id);
  if (status === 'updated') return 'success';
  if (status === 'unchanged' || status === 'pending') return 'info';
  if (status === 'failed') return 'danger';
  if (status === 'superseded') return 'warning';
  return '';
};

const typeBadge = type => {
  if (type === 'Client') return 'success';
  if (type === 'Prospect') return 'info';
  return '';
};

const isSelected = id => selectedIds.value.includes(id);

const toggleRow = id => {
  if (isSelected(id)) {
    selectedIds.value = selectedIds.value.filter(selectedId => selectedId !== id);
  } else {
    selectedIds.value = [...selectedIds.value, id];
  }
};

const toggleSelectAll = event => {
  const visibleIds = filteredRows.value.map(row => row.id);
  if (event.target.checked) {
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...visibleIds]));
  } else {
    selectedIds.value = selectedIds.value.filter(id => !visibleIds.includes(id));
  }
};

const stopPoll = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPoll = taskId => {
  if (pollTimer || !taskId) {
    return;
  }
  pollTimer = setInterval(async () => {
    try {
      const response = await fetch(`/api/v1/tasks/${taskId}/`, {
        method: 'GET',
        headers: advisorStore.headers,
      });
      const data = await response.json();
      const task = data.task;
      if (!task) {
        return;
      }
      applyLatest({
        status: task.status,
        meta: task.meta || {},
      });
      if (!['pending', 'started'].includes(task.status)) {
        stopPoll();
        isUpdating.value = false;
      }
    } catch (error) {
      console.error('Failed to poll bulk Redtail update', error);
    }
  }, 2000);
};

const load = async () => {
  isLoadingPeople.value = true;
  errorMessage.value = '';
  try {
    const response = await fetch('/api/v1/dossier/bulk-redtail-update/', {
      method: 'GET',
      headers: advisorStore.headers,
    });
    const data = await response.json();
    if (!response.ok) {
      errorMessage.value = data?.detail || data?.error || 'Failed to load Redtail-linked people.';
      return;
    }
    people.value = data.people || [];
    applyLatest(data.latest);
    if (data.latest && ['pending', 'started'].includes(data.latest.status)) {
      isUpdating.value = true;
      startPoll(data.latest.task_id);
    }
  } catch (error) {
    console.error('Failed to load Redtail-linked people', error);
    errorMessage.value = 'Failed to load Redtail-linked people.';
  } finally {
    isLoadingPeople.value = false;
  }
};

const updateSelected = async () => {
  if (!canUpdate.value) {
    return;
  }
  errorMessage.value = '';
  isUpdating.value = true;
  stopPoll();
  try {
    const result = await awaitTask('/api/v1/dossier/bulk-redtail-update/', {
      method: 'POST',
      headers: advisorStore.headers,
      body: JSON.stringify({ entity_ids: selectedIds.value }),
    }, 2000, task => {
      applyLatest({
        status: task.status,
        meta: task.meta || {},
      });
    });
    applyLatest({
      status: result.task?.status,
      meta: result.task?.meta || {},
    });
  } catch (error) {
    console.error('Bulk Redtail dossier update failed', error);
    if (error?.task?.meta) {
      applyLatest({
        status: error.task.status,
        meta: error.task.meta,
      });
    }
    errorMessage.value = error.message || 'Bulk Redtail update failed.';
  } finally {
    isUpdating.value = false;
  }
};

onMounted(load);
onBeforeUnmount(stopPoll);
</script>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 52rem;
  width: 70vw;
}

.help-text {
  color: var(--stk-text-60);
  font-size: 0.875em;
}

.counts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.count {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.count-label {
  font-size: 0.75em;
  color: var(--stk-text-60);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.search-input {
  min-width: 14em;
}

.empty,
.ready {
  color: var(--stk-text-60);
  font-size: 0.875em;
}

.table-wrap {
  max-height: 24rem;
  overflow: auto;
  border-top: 1px solid var(--stk-text-85);
}

.table {
  border-bottom: 1px solid var(--stk-text-85);
  width: 100%;
}

.head {
  background-color: hsl(var(--stk-text-h) var(--stk-text-s) 98%);
}

.col-head {
  color: var(--stk-text-60);
  font-weight: 500;
  font-size: 0.75em;
  padding: 0;
}

.col-head-button {
  align-items: center;
  display: flex;
  padding: 0.25rem 0.75rem;
}

.head .col-head.is-select .col-head-button {
  justify-content: center;
  min-height: 2.25rem;
}

.row {
  border-top: 1px solid var(--stk-text-90);
}

.cell {
  vertical-align: middle;
  padding: 0.75rem;
}

.is-select {
  width: 3rem;
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.row-error {
  color: var(--stk-danger, #c53030);
  font-size: 0.75em;
}
</style>
